import React, { useCallback, useMemo } from 'react';
import {
  BreakpointQueryName, Button, ButtonColor, ButtonVariant, Pagination, Spacing, SpacingName,
} from '@doveit/lego';
import { NextPage } from 'next';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import { PAGINATED_NEWS_QUERY } from '../client/graphql/queries/news';
import { mapArticles } from '../client/graphql/mappers/articleMappers';
import ArticlesList from '../client/components/articles-list/ArticlesList';
import {
  goToPage, buildNewsSerpURLObject,
} from '../client/utils/links';
import { buildPageableSEO } from '../client/utils/seo/seo';
import { getStructuredData, getBreadcrumbStructuredData } from '../data/structured';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { ArticleType } from '../domain/articleType';
import { AdvFluidType, New } from '../domain/graphcms';
import { Adv } from '../domain/adv';
import { ArticlesBreadcrumb } from '../client/constants';
import { getAdvByType } from '../services/advs/advs';
import { BreadcrumbItem } from '../client/components/breadcrumb/Breadcrumb';
import RelatedArticles, { RelatedArticleItem } from '../client/components/related-articles/RelatedArticles';
import AdvFluid from '../client/components/adv-fluid/AdvFluid';
import { BANNER_SELL_PROPERTY } from '../client/themes/icons';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import BottomBarV2 from '../client/templates/common/bottom-bar-v2/BottomBarV2';
import useRefInView from '../client/hooks/useRefInView/useRefInView';
import * as styles from '../client/templates/news-serp/NewsSerp.style';
import { BottomBarData } from '../data/bottom-bars';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { importDynamicData } from '../services/importDynamicData';

export interface NewsSERPProps {
  SEO: PageHeadProps['SEO'],
  bottomBar: BottomBarData,
  news: New[],
  page: number,
  totalPages: number,
  totalItems: number,
  advSellProperty: Adv,
  advSideSellProperty: Adv,
  promotionalLinks: Omit<RelatedArticleItem, 'onClick'>[],
}

export const PAGE_SIZE = 30;
const INTERSECTION_OPTIONS = { threshold: 1 };

export const NewsSERP: NextPage<NewsSERPProps> = ({
  SEO,
  news,
  page,
  totalPages,
  totalItems,
  advSellProperty,
  advSideSellProperty,
  bottomBar,
  promotionalLinks,
}) => {
  const { ref: headerRef, entry: header } = useRefInView(INTERSECTION_OPTIONS);
  const { ref: relatedArticlesRef, entry: relatedArticles } = useRefInView(INTERSECTION_OPTIONS);

  const breadcrumb = useMemo<BreadcrumbItem[]>(() => [{
    label: ArticlesBreadcrumb[ArticleType.NEWS],
    urlObject: buildNewsSerpURLObject(),
  }], []);

  const advInsertion = useCallback((index) => (
    index === 2 && (
      <AdvFluid
        {...advSellProperty}
        symbolPath={BANNER_SELL_PROPERTY}
        dataRef="adv-fluid-v1-main-sell-properties"
        dataTracking="adv-fluid-v1-main-sell-properties"
      />
    )
  ), [advSellProperty]);

  const getPageUrl = useCallback(
    (requestedPage) => buildNewsSerpURLObject({ p: requestedPage }).as,
    [],
  );
  const onPageChange = useCallback(
    (requestedPage) => goToPage(buildNewsSerpURLObject({ p: requestedPage })),
    [],
  );

  const goToBottomBarLink = useCallback(
    () => goToPage(bottomBar.urlObject),
    [bottomBar],
  );

  return (
    <AppLayout activePage="news">
      <PageHead
        SEO={SEO}
        structuredData={getStructuredData(getBreadcrumbStructuredData(breadcrumb))}
      />
      <PageLayout>
        <PageLayout.Wrapper>
          <PageLayout.Header>
            <h1
              css={styles.title}
              ref={headerRef}
            >
              Notizie immobiliari e news di Dove.it
            </h1>
          </PageLayout.Header>
          <PageLayout.Content>
            <PageLayout.Main>
              <ArticlesList
                articles={mapArticles(news, ArticleType.NEWS)}
                render={{ inPosition: advInsertion }}
              />
              {(page && totalItems && totalPages > 1) && (
                <div css={styles.pagination}>
                  {page > 2 && (
                    <div css={styles.paginationFirstPage}>
                      <Button
                        dataRef="first-page-button"
                        label="1"
                        onClick={() => onPageChange(1)}
                        href={getPageUrl(1)}
                        variant={ButtonVariant.LIGHT}
                        color={ButtonColor.NEUTRAL}
                        autoWidth
                        expanded
                      />
                    </div>
                  )}
                  <Pagination
                    current={page}
                    totalItems={totalItems}
                    pageSize={PAGE_SIZE}
                    navigateTo={onPageChange}
                    buildPagePath={getPageUrl}
                    currentPageAround={0}
                  />
                </div>
              )}
            </PageLayout.Main>
            <PageLayout.Aside>
              <AdvFluid
                {...advSideSellProperty}
                symbolPath={BANNER_SELL_PROPERTY}
                dataRef="adv-fluid-v1-aside-sell-property"
                dataTracking="adv-fluid-v1-aside-sell-property"
              />

              <Spacing
                ref={relatedArticlesRef}
                margin={[SpacingName.MEDIUM, 0, 0, 0]}
              >
                <RelatedArticles
                  title="Vedi anche"
                  items={promotionalLinks}
                />
              </Spacing>

              <div
                $visible={relatedArticles?.boundingClientRect.y < 0}
                css={styles.stickySideAdv}
                data-ref="adv-fluid-sticky-aside"
              >
                <AdvFluid
                  {...advSideSellProperty}
                  symbolPath={BANNER_SELL_PROPERTY}
                  dataRef="adv-fluid-v1-sticky-sell-property"
                  dataTracking="adv-fluid-v1-sticky-sell-property"
                />
              </div>
            </PageLayout.Aside>
          </PageLayout.Content>
          <div css={styles.footer}>
            <PageLayout.Breadcrumb items={breadcrumb} />
          </div>
        </PageLayout.Wrapper>

        <BottomBarV2
          hiddenFrom={BreakpointQueryName.LG}
          visible={header?.boundingClientRect.y < 0}
          label={bottomBar.label}
          title={bottomBar.title}
          href={bottomBar.urlObject?.as}
          onClick={goToBottomBarLink}
        />
      </PageLayout>
    </AppLayout>
  );
};
export const getServerSideProps = withGetServerSideError<NewsSERPProps>(async ({ query }) => {
  const graphQLClient = initGraphQLClient();
  const newsSerpURLObject = buildNewsSerpURLObject();

  const page = Number.parseInt(query.p as string || '1', 10);
  if (Number.isNaN(page) || page < 1) {
    return {
      redirect: {
        destination: newsSerpURLObject.as,
        permanent: false,
      },
    };
  }

  const graphqlQuery = {
    query: PAGINATED_NEWS_QUERY,
    variables: {
      skip: PAGE_SIZE * (page - 1),
      first: PAGE_SIZE,
    },
  };

  const [
    { SEO: seoTemplate, bottomBar, promotionalLinks },
    adv,
    { data },
  ] = await Promise.all([
    importDynamicData('pages/news-serp.yml'),
    getAdvByType(AdvFluidType.SELL_PROPERTY),
    graphQLClient.query(graphqlQuery),
  ]);

  const totalItems = data.total.aggregate.count;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  if (page > totalPages) {
    return {
      redirect: {
        destination: newsSerpURLObject.as,
        permanent: false,
      },
    };
  }

  const pageable = {
    page,
    first: page === 1,
    last: page === totalPages,
    hasResults: data.news.length > 0,
  };

  return ({
    props: {
      advSellProperty: adv,
      advSideSellProperty: adv,
      SEO: buildPageableSEO({
        ...seoTemplate,
        urlObject: newsSerpURLObject,
      }, pageable),
      news: data.news,
      page,
      totalPages,
      totalItems,
      bottomBar,
      promotionalLinks: promotionalLinks ?? [],
    },
  });
});

export default withError(NewsSERP);
